/*
 * GuidingStep — per-observation guiding controls (issue #673).
 *
 * Three switches map to plugin-recognized GuidingOptions fields:
 *   guidingEnabled  → !disable_guiding   (default: true,  use guiding when available)
 *   requireGuiding  →  force_guiding     (default: false, fail observation if guider missing)
 *   ditherEnabled   →  dither_enabled    (default: true,  dither between exposures)
 *
 * Companion to AutofocusStep — both control optional equipment behavior at
 * the observation level.
 *
 * The step is skipped entirely (controls hidden) when no candidate observatory
 * supports guiding — either no guider hardware (capabilities.guiding=false) or
 * plugin version too old to honor the controls.
 */

import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Grid,
  Alert,
  FormHelperText,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

// Minimum plugin version that honors GuidingOptions.DisableGuiding /
// ForceGuiding via Phase 2 wrappers. Anything older parses the fields but
// doesn't act on them.
const MIN_PLUGIN_VERSION = [3, 11, 0, 0];

function parseVersion(v) {
  if (!v || typeof v !== 'string') return null;
  const parts = v.split('.').map(p => parseInt(p, 10));
  if (parts.some(isNaN)) return null;
  while (parts.length < 4) parts.push(0);
  return parts.slice(0, 4);
}

function versionGte(a, b) {
  for (let i = 0; i < 4; i++) {
    if (a[i] > b[i]) return true;
    if (a[i] < b[i]) return false;
  }
  return true;
}

export function observatorySupportsGuiding(observatory) {
  if (!observatory?.capabilities?.guiding) return false;
  const v = parseVersion(observatory.plugin_version);
  if (!v) return false;
  return versionGte(v, MIN_PLUGIN_VERSION);
}

const GuidingStep = ({ observatories = [] }) => {
  const { watch, setValue } = useFormContext();

  const guidingEnabled = watch('guidingEnabled') !== false;
  const requireGuiding = watch('requireGuiding') === true;
  const ditherEnabled = watch('ditherEnabled') !== false;

  // Per-observatory gating: the form has already picked a target observatory
  // via `assignedTo`. Check just that one — guiding capability is per-rig.
  const assignedTo = watch('assignedTo');
  const targetObservatory = observatories.find(o => o._id === assignedTo) || null;

  if (!targetObservatory) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Guiding
        </Typography>
        <Alert severity="info">
          Select an observatory in the Project step before configuring guiding.
        </Alert>
      </Box>
    );
  }

  if (!observatorySupportsGuiding(targetObservatory)) {
    const reason = !targetObservatory.capabilities?.guiding
      ? "doesn't have a guider connected (capabilities.guiding is false)"
      : `is running plugin v${targetObservatory.plugin_version || 'unknown'} which doesn't support guiding control (need v3.11.0+)`;
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Guiding
        </Typography>
        <Alert severity="info">
          <strong>{targetObservatory.name}</strong> {reason}.
          Guiding controls don't apply for this observatory.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Guiding
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Control whether this observation uses autoguiding via PHD2.
        Defaults are sensible for typical long-exposure work.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={guidingEnabled}
                  onChange={(e) => setValue('guidingEnabled', e.target.checked)}
                />
              }
              label="Use guiding"
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              Off → exposures run un-guided regardless of guider state.
            </FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={requireGuiding}
                  onChange={(e) => setValue('requireGuiding', e.target.checked)}
                  disabled={!guidingEnabled}
                />
              }
              label="Require guiding"
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              On → the observation fails if a guider isn't connected at start time.
              Off (default) → un-guided fallback is acceptable.
            </FormHelperText>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={ditherEnabled}
                  onChange={(e) => setValue('ditherEnabled', e.target.checked)}
                  disabled={!guidingEnabled}
                />
              }
              label="Dither between frames"
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              Slight repointing between exposures to spread hot/cold pixels across the frame.
              Uses the dither amount configured in NINA's profile.
            </FormHelperText>
          </Grid>
        </Grid>
      </Paper>

      {guidingEnabled && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
          <strong>{targetObservatory.name}</strong> supports guiding via PHD2
          (plugin v{targetObservatory.plugin_version}). PHD2 must be connected
          in NINA's equipment selector at execution time. Other guider drivers
          (MetaGuide, MGEN, Direct, SkyGuard) are not currently supported.
        </Alert>
      )}
    </Box>
  );
};

export default GuidingStep;
